import { ChangeDetectorRef, Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { debounceTime, map, Observable, startWith } from 'rxjs';
import { CountryZone } from 'src/app/models/country_zone.model';
import { User } from 'src/app/models/user.model';
import { WorkingContractYearCondition } from 'src/app/models/working_contract_year_condition.model';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-local-holidays',
  templateUrl: './local-holidays.component.html',
  styleUrl: './local-holidays.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOption,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatButton,
  ],
})
export class LocalHolidaysComponent implements OnInit {
  private _api = inject(ApiService);
  private _utils = inject(UtilsService);
  private _userService = inject(UserService);
  private _snack = inject(MatSnackBar);
  private _fb = inject(FormBuilder);
  private cd = inject(ChangeDetectorRef);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  currentYear: number = new Date().getFullYear();

  dataSource: MatTableDataSource<WorkingContractYearCondition> = new MatTableDataSource();
  displayedColumns: string[] = ['working_contract_id', 'year', 'holidays_calendar_country_zone_id'];

  private initial_country_zones: CountryZone[] = [];
  country_zones: CountryZone[] = [];

  autocompleteSearchInputControls: FormControl[] = [];

  ngOnInit(): void {
    this.fetchMyWorkingContractYearConditions();
  }

  onAutocompleteClickCountryZone(evt: MatAutocompleteSelectedEvent, idx: number) {
    this.dataSource.data[idx].holidays_calendar_country_zone_id = (evt.option.value as CountryZone).id;
    this.dataSource.data[idx].holidays_calendar_country_zone = evt.option.value as CountryZone;

    this._api
      .updateWorkingContractYearCondition(this.dataSource.data[idx].id, {
        holidays_calendar_country_zone_id: (evt.option.value as CountryZone).id,
      })
      .subscribe({
        next: () => {
          this._snack.open('Calendario festivo actualizado correctamente', 'Ok', { duration: 3000 });
        },
        error: () => {
          this._snack.open('Error al actualizar el calendario festivo', 'Ok', { duration: 3000 });
        },
      });
  }

  displayFn(cz: CountryZone): string {
    return cz.name ?? '';
  }

  private fetchMyWorkingContractYearConditions() {
    const user_id = this._userService.getCurrentUser().id;
    this._api
      .getUserWorkingContractYearConditions(user_id, { order_by: 'year:desc', with: 'holidays_calendar_country_zone' })
      .subscribe(async data => {
        this.autocompleteSearchInputControls = data.map(item => {
          const fc = new FormControl(item.holidays_calendar_country_zone);
          fc.patchValue(item.holidays_calendar_country_zone);
          if (item.year < this.currentYear || (item.year == this.currentYear && item.holidays_calendar_country_zone_id != null))
            fc.disable();
          return fc;
        });
        this.dataSource.data = data;

        const rawCountryZones = data.map(item => item.holidays_calendar_country_zone);

        this.initial_country_zones = Array.from(new Map(rawCountryZones.map(item => [item.name, item])).values());

        this.initOnChangeInputAutocomplete();

        await this._utils.sleep(1000);
        this.cd.detectChanges();
      });
  }

  private initOnChangeInputAutocomplete() {
    this.autocompleteSearchInputControls.forEach((fc: FormControl) => {
      fc.valueChanges.pipe(debounceTime(500)).subscribe((val: string) => {
        if (val.length > 3) {
          // busquem contra API
          this._api.getCountryZones({ name: val, with: 'parent' }).subscribe(data => {
            this.country_zones = data;
          });
        } else {
          this.country_zones = this.initial_country_zones.slice();
        }
      });
    });
  }
}
